<script>
import { defineAsyncComponent, ref } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import VerticalTopNavigation from 'next/sidebar/VerticalTopNavigation.vue';
import DashboardBreadcrumb from 'dashboard/components-next/breadcrumb/DashboardBreadcrumb.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';
import GuidedTourOverlay from './tour/GuidedTourOverlay.vue';
import PlatformAnnouncementToast from 'dashboard/components-next/PlatformAnnouncementToast.vue';
import { trackClientLocation } from 'dashboard/api/clientLocations';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useDashboardLayout } from 'dashboard/composables/useDashboardLayout';
import { useDashboardFullscreen } from 'dashboard/composables/useDashboardFullscreen';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';
import { isUpgradePageBypassRoute } from 'dashboard/helper/routeHelpers';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

const CONVERSATION_ROUTES = [
  'inbox_conversation',
  'conversation_through_inbox',
  'conversations_through_label',
  'team_conversations_through_label',
  'conversations_through_folders',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversation_through_resolved',
  'conversation_through_participating',
  'inbox_view_conversation',
];

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
export default {
  components: {
    NextSidebar,
    VerticalTopNavigation,
    DashboardBreadcrumb,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    GuidedTourOverlay,
    PlatformAnnouncementToast,
    FloatingCallWidget,
    MobileSidebarLauncher,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { layout: dashboardLayout, container: dashboardContainer } =
      useDashboardLayout();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const globalConfig = useMapGetter('globalConfig/get');

    return {
      fullscreen: useDashboardFullscreen(),
      uiSettings,
      updateUISettings,
      dashboardLayout,
      dashboardContainer,
      accountId,
      globalConfig,
      upgradePageRef,
      windowWidth,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      isTalkviaSupportOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    isHorizontalLayout() {
      return this.dashboardLayout === 'horizontal' && this.windowWidth >= 1280;
    },
    isVerticalDesktop() {
      return !this.isHorizontalLayout && !this.isSmallScreen;
    },
    isBoxedContainer() {
      return (
        this.dashboardContainer === 'boxed' &&
        !this.fullscreen?.isExpanded.value
      );
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    isAccountPaywalled() {
      return this.upgradePageRef?.isAccountPaywalled;
    },
    bypassUpgradePage() {
      return isUpgradePageBypassRoute(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    talkviaSupportLabel() {
      return this.globalConfig?.talkviaSupportLabel || 'Suporte Talkvia';
    },
    skipToContentLabel() {
      return 'Pular para o conteúdo';
    },
    talkviaSupportButtonLabel() {
      return this.isTalkviaSupportOpen ? 'Fechar' : this.talkviaSupportLabel;
    },
    closeTalkviaSupportLabel() {
      return 'Fechar suporte Talkvia';
    },
    isConversationRoute() {
      return (
        CONVERSATION_ROUTES.includes(this.$route.name) ||
        this.$route.matched.some(record => record.meta?.conversationScreen)
      );
    },
    showGlobalBreadcrumb() {
      return (
        !this.isConversationRoute &&
        !(
          this.$route.meta.restaurantTab === 'kitchen' &&
          this.$route.query.kitchen_expanded === '1'
        )
      );
    },
    showTalkviaSupportButton() {
      const config = this.globalConfig || {};
      return Boolean(
        !this.isConversationRoute &&
          config.talkviaSupportEnabled &&
          (config.talkviaSupportUrl ||
            (config.talkviaSupportWidgetBaseUrl &&
              config.talkviaSupportWidgetToken) ||
            window.$chatwoot)
      );
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.trackAccessLocation();
    window.addEventListener('chatwoot:opened', this.markTalkviaSupportOpen);
    window.addEventListener('chatwoot:closed', this.markTalkviaSupportClosed);
  },
  beforeUnmount() {
    window.removeEventListener('chatwoot:opened', this.markTalkviaSupportOpen);
    window.removeEventListener(
      'chatwoot:closed',
      this.markTalkviaSupportClosed
    );
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    trackAccessLocation() {
      if (!this.accountId) return;

      const storageKey = `talkvia:client-location:v2:${this.accountId}`;
      const trackedAt = Number(window.localStorage.getItem(storageKey) || 0);
      const shouldSkip = Date.now() - trackedAt < 60 * 60 * 1000;
      if (shouldSkip) return;

      const markTracked = () => {
        window.localStorage.setItem(storageKey, Date.now().toString());
      };

      trackClientLocation({ accountId: this.accountId })
        .then(markTracked)
        .catch(() => {});

      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        position => {
          trackClientLocation({
            accountId: this.accountId,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              source: 'browser',
            },
          })
            .then(markTracked)
            .catch(() => {});
        },
        () => {
          trackClientLocation({
            accountId: this.accountId,
            location: { source: 'ip' },
          })
            .then(markTracked)
            .catch(() => {});
        },
        { enableHighAccuracy: false, maximumAge: 60 * 60 * 1000, timeout: 8000 }
      );
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    markTalkviaSupportOpen() {
      this.isTalkviaSupportOpen = true;
    },
    markTalkviaSupportClosed() {
      this.isTalkviaSupportOpen = false;
    },
    closeTalkviaSupport() {
      if (!this.isTalkviaSupportOpen) return;

      window.$chatwoot?.toggle('close');
    },
    openTalkviaSupport() {
      const config = this.globalConfig || {};
      if (config.talkviaSupportUrl) {
        window.open(config.talkviaSupportUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (window.$chatwoot) {
        window.$chatwoot.toggle(this.isTalkviaSupportOpen ? 'close' : 'open');
        return;
      }

      this.loadTalkviaSupportWidget();
    },
    hideTalkviaSupportWidgetLauncher() {
      if (document.getElementById('talkvia-support-widget-style')) return;

      const style = document.createElement('style');
      style.id = 'talkvia-support-widget-style';
      style.textContent = `
        .woot-widget-bubble{display:none!important;}
        .woot-widget-holder.woot-elements--right{right:20px!important;}
        .woot-widget-holder.woot-elements--left{left:20px!important;}
        @media only screen and (min-width: 667px){
          .woot-widget-holder{bottom:72px!important;}
        }
      `;
      document.head.appendChild(style);
    },
    loadTalkviaSupportWidget() {
      const config = this.globalConfig || {};
      const baseUrl = config.talkviaSupportWidgetBaseUrl;
      const websiteToken = config.talkviaSupportWidgetToken;
      if (!baseUrl || !websiteToken) return;

      this.hideTalkviaSupportWidgetLauncher();
      const openWidget = () => window.$chatwoot?.toggle('open');
      window.addEventListener('chatwoot:ready', openWidget, { once: true });

      if (window.chatwootSDK) {
        window.chatwootSDK.run({ websiteToken, baseUrl });
        return;
      }

      const existingScript = document.getElementById('talkvia-support-widget');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'talkvia-support-widget';
      script.async = true;
      script.src = `${baseUrl.replace(/\/$/, '')}/packs/js/sdk.js`;
      script.onload = () => window.chatwootSDK?.run({ websiteToken, baseUrl });
      document.body.appendChild(script);
    },
  },
};
</script>

<template>
  <div
    class="min-h-0 flex-grow overflow-hidden bg-n-surface-1 text-n-slate-12 md:gap-2 md:p-2"
    :class="{
      '!p-0 !gap-0': fullscreen?.isExpanded.value,
      'flex flex-col': isHorizontalLayout,
      'grid grid-cols-[auto_minmax(0,1fr)] grid-rows-[4rem_minmax(0,1fr)]':
        isVerticalDesktop,
      'flex flex-row': !isHorizontalLayout && !isVerticalDesktop,
    }"
  >
    <a
      href="#dashboard-main-content"
      class="fixed left-3 top-3 z-[100] -translate-y-20 rounded-lg bg-n-background px-4 py-2 font-medium text-n-slate-12 shadow-lg transition-transform focus:translate-y-0"
    >
      <span>{{ skipToContentLabel }}</span>
    </a>
    <VerticalTopNavigation
      v-if="isVerticalDesktop"
      :boxed="isBoxedContainer"
      @customize-navigation="$refs.sidebarRef.openCustomization()"
      @show-create-account-modal="openCreateAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
    />
    <NextSidebar
      ref="sidebarRef"
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      :layout-mode="dashboardLayout"
      :container-mode="dashboardContainer"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      id="dashboard-main-content"
      data-tour-global-target
      class="flex min-h-0 w-full flex-1 overflow-hidden bg-transparent px-0"
      :class="
        isHorizontalLayout || isVerticalDesktop
          ? 'rounded-2xl border border-n-weak shadow-sm'
          : ''
      "
      tabindex="-1"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <div
          class="flex min-h-0 min-w-0 flex-1 flex-col"
          :class="
            isBoxedContainer
              ? 'mx-auto w-full max-w-[1440px] bg-n-background'
              : 'w-full'
          "
        >
          <DashboardBreadcrumb v-if="showGlobalBreadcrumb" />
          <router-view class="min-h-0 min-w-0 flex-1" />
        </div>
        <CommandBar />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <FloatingCallWidget />
        <GuidedTourOverlay />
        <PlatformAnnouncementToast />
        <button
          v-if="isTalkviaSupportOpen"
          type="button"
          class="fixed inset-0 z-30 cursor-default bg-transparent"
          :aria-label="closeTalkviaSupportLabel"
          @click="closeTalkviaSupport"
        />
        <button
          v-if="showTalkviaSupportButton"
          type="button"
          class="talkvia-support-button fixed bottom-5 right-5 z-40 hidden h-11 items-center gap-2 rounded-full bg-n-solid-2 px-4 text-sm font-medium text-n-slate-12 transition hover:bg-n-solid-3 focus:outline-none focus:ring-2 focus:ring-n-brand/30 md:inline-flex"
          @click="openTalkviaSupport"
        >
          <span class="i-lucide-headphones h-4 w-4" />
          <span>{{ talkviaSupportButtonLabel }}</span>
        </button>
      </template>
      <CommandBar :is-paywalled="isAccountPaywalled" />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>

<style scoped>
.talkvia-support-button {
  border: 1px solid rgba(96, 165, 250, 0.32);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(96, 165, 250, 0.12),
    inset 0 0 10px rgba(45, 212, 191, 0.08);
}

.talkvia-support-button::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    110deg,
    rgba(45, 212, 191, 0.34),
    rgba(59, 130, 246, 0.08),
    rgba(96, 165, 250, 0.32)
  );
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  padding: 1px;
}
</style>
